import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName?: string;
  profilePicture?: string;
  createdAt?: string;
  role?: string;
  platformRole?: string;
  organizationId?: string;
  organizationName?: string;
  defaultCurrency?: string;
  planType?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') as string) : null,
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
      // Persist to localStorage as backup (server now sets httpOnly session cookie too)
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('platformRole');
      localStorage.removeItem('currentWorkspaceId');
      localStorage.removeItem('sa_original_token');
      localStorage.removeItem('sa_original_user');
      // Server clears session cookie via POST /auth/logout
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUserCurrency: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.defaultCurrency = action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    updateUserProfile: (state, action: PayloadAction<{ firstName?: string; lastName?: string }>) => {
      if (state.user) {
        if (action.payload.firstName !== undefined) state.user.firstName = action.payload.firstName;
        if (action.payload.lastName !== undefined) state.user.lastName = action.payload.lastName;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
});

export const { setCredentials, logout, setLoading, setError, clearError, updateUserCurrency, updateUserProfile } = authSlice.actions;
export default authSlice.reducer;
